import { useEffect, useId, useRef, useState } from 'react';
import type React from 'react';
import { ArrowLeft, ArrowRight, Check, CheckCircle2, ChevronDown, Clock, Mail, RefreshCw, Send } from 'lucide-react';
import { readOnlyTitle } from '../../../services/readOnlyNotice';
import { empfaengerVonHand, pruefeEmpfaengerEingabe, type EmpfaengerEintrag } from '../empfaenger';
import { FolgeunterlagenKarte } from '../FolgeunterlagenKarte';
import { abschlussRueckfrage, gewaehlteArten, istGewaehlt, type FolgeHaken } from '../folgeunterlagenAnzeige';
import type { MerkmalSchalter } from '../planungVorbelegung';
import { datumDeutsch } from '../unterlagenLogic';
import { ANHANG2_LABEL, LPH_LABEL, type DialogFeld } from './dialogFragen';
import type {
    AbgeschlossenKarteProps,
    AbschliessenKarteProps,
    BedenkenKarteProps,
    BeschreibungKarteProps,
    BestaetigenKarteProps,
    DialogRahmenProps,
    EmpfaengerKarteProps,
    FrageAntwort,
    FrageKarteProps,
    FristenKarteProps,
    GegenstandslosKarteProps,
    HinweisKarteProps,
    KatalogKarteProps,
    KiLiestKarteProps,
    UnterlagenKarteProps,
    VersendenKarteProps,
    WartenKarteProps,
} from './dialogKartenTypen';
import { DIALOG_ABSCHNITT_LABEL, type BestaetigungsZeile } from './dialogLogik';
import { Aufklapper, GefuehrtRahmen, Handlungen, Kopf, Kreisel } from '../../common/gefuehrt/GefuehrtRahmen';
import {
    FELD, FELD_RAHMEN, FOKUS, KNOPF_HAUPT, KNOPF_LEISE, KNOPF_WAHL, WAHL_AN, WAHL_AUS, WAHL_GROSS,
} from '../../common/gefuehrt/klassen';

/**
 * Die Karten des gefuehrten Dialogs — reine Darstellung nach dem Vertrag in
 * `dialogKartenTypen.ts`. Jede Karte stellt EINE Frage oder nennt EINE
 * Aufgabe: hoechstens ein Satz Erklaerung, genau ein grosser Hauptknopf,
 * hoechstens zwei leise Textknoepfe. Paragraphen stehen nur hinter „Warum?".
 *
 * Eigener Zustand ist nur Eingabe (Textfeld, Haken, Auswahl). Welche Karte
 * dran ist und was ein Klick bewirkt, entscheidet `PlanungDialog.tsx`.
 */

// ═══════════════════════════════════════════════════════════════════════════
// BAUSTEINE
// ═══════════════════════════════════════════════════════════════════════════

// Rahmen, Knopfklassen, Kopf, Handlungen und Aufklapper liegen seit dem
// 02.11.2026 in `common/gefuehrt/GefuehrtRahmen.tsx` — die Baustellenordnung
// benutzt denselben Rahmen. Hier bleibt nur, was die Planungsphase allein
// braucht.

/** Die KI konnte nicht lesen — eine amberfarbene Zeile. */
const KiFehlerZeile: React.FC<{ text: string; klein?: boolean }> = ({ text, klein }) => (
    <p className={`mt-3 ${klein ? 'text-xs' : 'text-sm'} text-amber-700 leading-snug break-words`}>
        Die KI konnte die Beschreibung nicht lesen: {text}
    </p>
);

// ═══════════════════════════════════════════════════════════════════════════
// RAHMEN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Rahmen des Dialogs: die vier Abschnitte der Planungsphase auf die Schritte
 * des gemeinsamen Rahmens abgebildet (`common/gefuehrt/GefuehrtRahmen.tsx`).
 */
export const DialogRahmen: React.FC<DialogRahmenProps> = ({ fortschritt, children, fuss }) => (
    <GefuehrtRahmen
        schritte={fortschritt.map(({ abschnitt, stand }) => ({ label: DIALOG_ABSCHNITT_LABEL[abschnitt], stand }))}
        fuss={fuss}
    >
        {children}
    </GefuehrtRahmen>
);

// ═══════════════════════════════════════════════════════════════════════════
// ABSCHNITT „VORHABEN"
// ═══════════════════════════════════════════════════════════════════════════

/** Die Beschreibung des Vorhabens — vorbelegt aus den Projektdaten, wenn vorhanden. */
export const BeschreibungKarte: React.FC<BeschreibungKarteProps> = ({ vorlage, ausProjekt, onWeiter }) => {
    const [text, setText] = useState(vorlage);
    const id = useId();
    const titelId = `${id}-titel`;
    const hinweisId = `${id}-hinweis`;
    return (
        <>
            <Kopf
                id={titelId}
                titel="Worum geht es bei diesem Bauvorhaben?"
                zeile={ausProjekt ? 'Aus den Projektdaten übernommen. Ergänzen Sie, was fehlt.' : 'Zwei, drei Sätze reichen.'}
            />
            <textarea
                value={text}
                onChange={e => setText(e.target.value)}
                rows={6}
                aria-labelledby={titelId}
                aria-describedby={hinweisId}
                className={`mt-4 ${FELD} leading-relaxed`}
            />
            <p id={hinweisId} className="mt-2 text-xs text-slate-500 leading-snug">
                Der Text geht zur Auswertung an die KI (Google Gemini). Bitte keine Namen, Anschriften oder Telefonnummern.
            </p>
            <Handlungen>
                <button type="button" onClick={() => onWeiter(text.trim())} disabled={!text.trim()} className={KNOPF_HAUPT}>
                    Weiter <ArrowRight className="w-5 h-5" />
                </button>
                <button type="button" onClick={() => onWeiter('')} className={KNOPF_LEISE}>
                    Ohne Beschreibung weiter
                </button>
            </Handlungen>
        </>
    );
};

/** Die KI liest die Beschreibung (startet von selbst). */
export const KiLiestKarte: React.FC<KiLiestKarteProps> = ({ onAbbrechen }) => (
    <>
        <Kopf
            rolle="status"
            icon={<Kreisel />}
            titel="Ich lese die Beschreibung …"
            zeile="Danach frage ich nur noch, was offen ist."
        />
        {/* Die KI liest von selbst (Betreiber 01.11.) — dann muss hier stehen,
            was dabei das Haus verlaesst, auch wenn die Beschreibung schon da war. */}
        <p className="mt-3 text-xs text-slate-500 leading-snug">
            Baubeschreibung und ergänzende Angaben gehen dazu an die KI (Google Gemini), sonst nichts aus dem Projekt.
        </p>
        <Handlungen>
            <button type="button" onClick={() => onAbbrechen()} className={KNOPF_LEISE}>
                Abbrechen
            </button>
        </Handlungen>
    </>
);

/** Ja gruen, Nein amber, Zahlen schlicht. */
const wertTon = (z: BestaetigungsZeile): string => {
    if (z.nein) return 'text-amber-700';
    return z.wertText === 'Ja' ? 'text-emerald-700' : 'text-slate-800';
};

/** Eine Zeile von „Das habe ich gefunden"; der Beleg klappt auf Tippen auf. */
const BestaetigungsEintrag: React.FC<{ zeile: BestaetigungsZeile; onVerwerfen: (id: string) => void }> = ({ zeile: z, onVerwerfen }) => {
    const [offen, setOffen] = useState(false);
    const belegId = useId();
    const quelle = `${z.quelle}${z.geschaetzt ? ' · geschätzt' : ''}`;
    const text = (
        <>
            <span className="block text-sm font-bold text-slate-800 leading-snug break-words">{z.label}</span>
            <span className="mt-0.5 flex items-center gap-1 text-xs text-slate-500 break-words">
                {quelle}
                {z.beleg && <ChevronDown aria-hidden="true" className={`w-3.5 h-3.5 shrink-0 transition-transform ${offen ? 'rotate-180' : ''}`} />}
            </span>
        </>
    );
    return (
        <li className="py-3 flex items-start gap-3">
            <div className="min-w-0 flex-1">
                {z.beleg ? (
                    <button
                        type="button"
                        onClick={() => setOffen(o => !o)}
                        aria-expanded={offen}
                        aria-controls={belegId}
                        className={`block w-full rounded-lg text-left ${FOKUS}`}
                    >
                        {text}
                    </button>
                ) : text}
                {z.beleg && (
                    <p id={belegId} hidden={!offen} className="mt-1 text-xs italic text-slate-600 leading-snug break-words">
                        {z.beleg}
                    </p>
                )}
            </div>
            <div className="shrink-0 text-right">
                <div className={`text-sm font-black ${wertTon(z)}`}>{z.wertText}</div>
                <button
                    type="button"
                    onClick={() => onVerwerfen(z.id)}
                    className={`min-h-8 rounded text-xs font-bold text-slate-400 hover:text-slate-700 ${FOKUS}`}
                >
                    Stimmt nicht
                </button>
            </div>
        </li>
    );
};

/** Alles, was Projektdaten und KI uebereinstimmend sagen — auf einmal bestaetigen. */
export const BestaetigenKarte: React.FC<BestaetigenKarteProps> = ({ zeilen, kiFehler, onStimmtSo, onVerwerfen }) => {
    const titelId = useId();
    return (
        <>
            <Kopf id={titelId} titel="Das habe ich gefunden" zeile="Stimmt das so?" />
            {kiFehler && <KiFehlerZeile text={kiFehler} />}
            <ul aria-labelledby={titelId} className="mt-4 divide-y divide-slate-100 border-y border-slate-100">
                {zeilen.map(z => <BestaetigungsEintrag key={z.id} zeile={z} onVerwerfen={onVerwerfen} />)}
            </ul>
            <Handlungen>
                <button type="button" onClick={() => onStimmtSo()} className={KNOPF_HAUPT}>
                    <Check className="w-5 h-5" /> Stimmt so
                </button>
            </Handlungen>
        </>
    );
};

// ─── Frage ─────────────────────────────────────────────────────────────────

type ZahlFeld = Extract<DialogFeld, { art: 'zahl' }>;
type AntwortFn = (a: FrageAntwort) => void;

const zahlDeutsch = (n: number): string => n.toLocaleString('de-DE');

/**
 * Liest eine Zahl so, wie man sie tippt: „5,5", „5.5", bei ganzen Zahlen
 * auch mit Tausenderpunkt („2.400"). Leer → `null` (kein Fehler, nur kein
 * Weiter).
 */
const leseZahl = (roh: string, feld: ZahlFeld): { wert: number } | { fehler: string } | null => {
    let t = roh.trim().replace(/\s+/g, '');
    if (!t) return null;
    if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.');
    else if (feld.ganzzahl && /^\d{1,3}(\.\d{3})+$/.test(t)) t = t.replace(/\./g, '');
    if (!/^-?(\d+\.?\d*|\.\d+)$/.test(t)) return { fehler: 'Bitte eine Zahl eintragen.' };
    const n = Number(t);
    if (!Number.isFinite(n)) return { fehler: 'Bitte eine Zahl eintragen.' };
    if (feld.ganzzahl && !Number.isInteger(n)) return { fehler: 'Bitte eine ganze Zahl eintragen.' };
    if (n < feld.min || n > feld.max) {
        return { fehler: `Bitte eine Zahl von ${zahlDeutsch(feld.min)} bis ${zahlDeutsch(feld.max)} eintragen.` };
    }
    return { wert: n };
};

const JaNeinAntwort: React.FC<{ titelId: string; onAntwort: AntwortFn }> = ({ titelId, onAntwort }) => (
    <div role="group" aria-labelledby={titelId} className="grid grid-cols-2 gap-3">
        <button type="button" onClick={() => onAntwort({ art: 'ja_nein', wert: true })} className={`${KNOPF_WAHL} ${WAHL_GROSS} ${WAHL_AUS} text-lg`}>
            Ja
        </button>
        <button type="button" onClick={() => onAntwort({ art: 'ja_nein', wert: false })} className={`${KNOPF_WAHL} ${WAHL_GROSS} ${WAHL_AUS} text-lg`}>
            Nein
        </button>
    </div>
);

/** Zahl mit Einheit; der Fehler erscheint erst nach Weiter, Enter oder Verlassen des Felds. */
const ZahlAntwort: React.FC<{ feld: ZahlFeld; titelId: string; onAntwort: AntwortFn }> = ({ feld, titelId, onAntwort }) => {
    const id = useId();
    const einheitId = `${id}-einheit`;
    const fehlerId = `${id}-fehler`;
    const [roh, setRoh] = useState('');
    const [versucht, setVersucht] = useState(false);
    const lesung = leseZahl(roh, feld);
    const fehler = versucht && lesung && 'fehler' in lesung ? lesung.fehler : null;
    const senden = () => {
        if (!lesung) return;
        setVersucht(true);
        if ('wert' in lesung) onAntwort({ art: 'zahl', wert: lesung.wert });
    };
    return (
        <div>
            <div className="flex items-center gap-3">
                <input
                    type="text"
                    inputMode={feld.ganzzahl ? 'numeric' : 'decimal'}
                    autoComplete="off"
                    enterKeyHint="next"
                    autoFocus
                    value={roh}
                    onChange={e => setRoh(e.target.value)}
                    onBlur={() => { if (roh.trim()) setVersucht(true); }}
                    onKeyDown={e => {
                        if (e.key !== 'Enter') return;
                        e.preventDefault();
                        senden();
                    }}
                    aria-labelledby={feld.einheit ? `${titelId} ${einheitId}` : titelId}
                    aria-invalid={fehler ? true : undefined}
                    aria-describedby={fehler ? fehlerId : undefined}
                    className={`${FELD_RAHMEN} max-w-[12rem] text-lg font-bold`}
                />
                {feld.einheit && <span id={einheitId} className="text-base font-bold text-slate-500">{feld.einheit}</span>}
            </div>
            {fehler && <p id={fehlerId} role="alert" className="mt-2 text-sm font-bold text-rose-700">{fehler}</p>}
            <Handlungen>
                <button type="button" onClick={senden} disabled={!lesung} className={KNOPF_HAUPT}>
                    Weiter <ArrowRight className="w-5 h-5" />
                </button>
            </Handlungen>
        </div>
    );
};

const LPH_NUMMERN = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const;

const LphAntwort: React.FC<{ titelId: string; onAntwort: AntwortFn }> = ({ titelId, onAntwort }) => (
    <div role="group" aria-labelledby={titelId} className="grid grid-cols-3 gap-2">
        {LPH_NUMMERN.map(n => (
            <button
                key={n}
                type="button"
                onClick={() => onAntwort({ art: 'lph', wert: n })}
                className={`${KNOPF_WAHL} ${WAHL_AUS} min-h-16 flex-col px-2 py-2`}
            >
                <span className="text-base">LPH {n}</span>
                <span className="mt-0.5 text-[11px] font-medium leading-tight text-slate-500 hyphens-auto break-words">{LPH_LABEL[n]}</span>
            </button>
        ))}
    </div>
);

const gruppeLabel = (k: MerkmalSchalter): string => ANHANG2_LABEL[k] || k;

/**
 * Mehrere seltene Merkmale: „Nein, nichts davon" setzt alle auf Nein, „Ja"
 * klappt die Haken auf. Ist nur noch eines offen, gilt „Ja" sofort.
 */
const GruppeAntwort: React.FC<{ felder: readonly MerkmalSchalter[]; titelId: string; onAntwort: AntwortFn }> = ({ felder, titelId, onAntwort }) => {
    const auswahlId = useId();
    const [auswahl, setAuswahl] = useState(false);
    const [ja, setJa] = useState<ReadonlySet<MerkmalSchalter>>(() => new Set());
    const gewaehlt = felder.filter(k => ja.has(k));
    const einzeln = felder.length === 1;
    const umschalten = (k: MerkmalSchalter, an: boolean) => setJa(alt => {
        const neu = new Set(alt);
        if (an) neu.add(k); else neu.delete(k);
        return neu;
    });
    return (
        <div>
            {!auswahl && (
                <ul className="mb-4 ml-5 list-disc space-y-1 text-sm text-slate-700 leading-snug">
                    {felder.map(k => <li key={k}>{gruppeLabel(k)}</li>)}
                </ul>
            )}
            <div role="group" aria-labelledby={titelId} className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => onAntwort({ art: 'gruppe', ja: [] })} className={`${KNOPF_WAHL} ${WAHL_GROSS} ${WAHL_AUS} text-base`}>
                    Nein, nichts davon
                </button>
                {einzeln ? (
                    <button type="button" onClick={() => onAntwort({ art: 'gruppe', ja: [felder[0]] })} className={`${KNOPF_WAHL} ${WAHL_GROSS} ${WAHL_AUS} text-base`}>
                        Ja
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={() => setAuswahl(a => !a)}
                        aria-pressed={auswahl}
                        aria-controls={auswahlId}
                        className={`${KNOPF_WAHL} ${WAHL_GROSS} ${auswahl ? WAHL_AN : WAHL_AUS} text-base`}
                    >
                        Ja
                    </button>
                )}
            </div>
            {auswahl && (
                <div id={auswahlId} className="mt-4">
                    <ul className="space-y-1">
                        {felder.map(k => (
                            <li key={k}>
                                <label className="flex min-h-11 cursor-pointer items-start gap-3 rounded-lg px-2 py-2 hover:bg-slate-50">
                                    <input
                                        type="checkbox"
                                        checked={ja.has(k)}
                                        onChange={e => umschalten(k, e.target.checked)}
                                        className="mt-0.5 w-5 h-5 shrink-0 accent-blue-600"
                                    />
                                    <span className="text-sm text-slate-800 leading-snug">{gruppeLabel(k)}</span>
                                </label>
                            </li>
                        ))}
                    </ul>
                    <p className="mt-2 text-xs text-slate-500">Nicht angehakte gelten als Nein.</p>
                    <Handlungen>
                        <button
                            type="button"
                            onClick={() => onAntwort({ art: 'gruppe', ja: gewaehlt })}
                            disabled={gewaehlt.length === 0}
                            className={KNOPF_HAUPT}
                        >
                            Übernehmen
                        </button>
                    </Handlungen>
                </div>
            )}
        </div>
    );
};

/** So lange nach dem Erscheinen einer Frage zaehlt kein Tipp. */
const SPERRE_NACH_ERSCHEINEN_MS = 400;

const FrageInhalt: React.FC<FrageKarteProps> = ({ frage, hinweise, nummer, gesamt, kiFehler, onAntwort: weiter, onZurueck }) => {
    const titelId = useId();
    const feld = frage.feld;
    // Doppeltippen: die naechste Frage erscheint sofort, ihre Knoepfe an
    // derselben Stelle — der zweite Tipp beantwortete sie ungesehen, ein
    // Nein blendete Fragen aus (Opus-Review 01.11.). Die Karte haengt je
    // Frage neu ein; kurz danach zaehlt kein Tipp.
    const bereitAb = useRef(Number.POSITIVE_INFINITY);
    // `performance.now()` laeuft stetig — eine zurueckgestellte Uhr sperrte sonst.
    useEffect(() => { bereitAb.current = performance.now() + SPERRE_NACH_ERSCHEINEN_MS; }, []);
    const onAntwort: AntwortFn = a => {
        if (performance.now() < bereitAb.current) return;
        weiter(a);
    };
    return (
        <>
            <Kopf id={titelId} ueber={`Frage ${nummer} von ${gesamt}`} titel={frage.frage} gross />
            {frage.erlaeuterung && <p className="mt-2 text-sm text-slate-600 leading-snug">{frage.erlaeuterung}</p>}
            {frage.warum && <Aufklapper label="Warum?">{frage.warum}</Aufklapper>}
            {hinweise.length > 0 && (
                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                    <p className="text-xs font-bold text-slate-600">Dazu steht im Projekt:</p>
                    <ul className="mt-1 space-y-1">
                        {hinweise.map((h, i) => (
                            <li key={`${i}:${h.wertText}:${h.quelle}`} className="text-sm text-slate-700 leading-snug break-words">
                                <span className="font-bold">{h.wertText}</span>
                                <span className="text-slate-500"> · {h.quelle}</span>
                                {h.beleg && <span className="block text-xs italic text-slate-500">{h.beleg}</span>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {kiFehler && <KiFehlerZeile text={kiFehler} klein />}
            <div className="mt-5">
                {feld.art === 'ja_nein' && <JaNeinAntwort titelId={titelId} onAntwort={onAntwort} />}
                {feld.art === 'zahl' && <ZahlAntwort feld={feld} titelId={titelId} onAntwort={onAntwort} />}
                {feld.art === 'lph' && <LphAntwort titelId={titelId} onAntwort={onAntwort} />}
                {feld.art === 'gruppe' && <GruppeAntwort felder={feld.felder} titelId={titelId} onAntwort={onAntwort} />}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-x-4">
                {onZurueck && (
                    <button type="button" onClick={() => onZurueck()} className={KNOPF_LEISE}>
                        <ArrowLeft className="w-4 h-4" /> Zurück
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => onAntwort({ art: 'weiss_nicht' })}
                    title="Bleibt offen. Die Fragen dazu gehen an die Beteiligten."
                    className={`${KNOPF_LEISE} ml-auto`}
                >
                    Weiß nicht
                </button>
            </div>
        </>
    );
};

/**
 * Eine Frage je Bildschirm — Ja/Nein, Zahl, Leistungsphase oder Gruppe.
 * Der Eingabezustand haengt an `frage.id` und beginnt bei jeder Frage neu.
 */
export const FrageKarte: React.FC<FrageKarteProps> = props => <FrageInhalt key={props.frage.id} {...props} />;

/** Der Fragenkatalog entsteht (von selbst); nach einem Fehlschlag „Erneut versuchen". */
export const KatalogKarte: React.FC<KatalogKarteProps> = ({ laeuft, fehlgeschlagen, onErneut }) => {
    if (fehlgeschlagen && !laeuft) {
        return (
            <>
                <Kopf rolle="alert" titel="Die Fragen konnten nicht angelegt werden." />
                <Handlungen>
                    <button type="button" onClick={() => onErneut()} className={KNOPF_HAUPT}>
                        <RefreshCw className="w-5 h-5" /> Erneut versuchen
                    </button>
                </Handlungen>
            </>
        );
    }
    return <Kopf rolle="status" icon={<Kreisel />} titel="Ich stelle die Fragen für dieses Projekt zusammen …" />;
};

// ═══════════════════════════════════════════════════════════════════════════
// ABSCHNITT „FRAGEN VERSCHICKEN"
// ═══════════════════════════════════════════════════════════════════════════

/** Noch nie gestellte Fragen, die nicht mehr zu den Angaben passen. */
export const GegenstandslosKarte: React.FC<GegenstandslosKarteProps> = ({ anzahl, laeuft, onSetzen, onSpaeter }) => {
    const eins = anzahl === 1;
    return (
        <>
            <Kopf
                titel={eins ? '1 Frage passt nicht mehr zu den Angaben' : `${anzahl} Fragen passen nicht mehr zu den Angaben`}
                zeile={eins ? 'Sie wurde noch nicht verschickt.' : 'Sie wurden noch nicht verschickt.'}
            />
            <Handlungen>
                <button
                    type="button"
                    onClick={() => onSetzen()}
                    disabled={laeuft}
                    title={eins ? 'Wird als gegenstandslos vermerkt, gelöscht wird nichts.' : 'Werden als gegenstandslos vermerkt, gelöscht wird nichts.'}
                    className={KNOPF_HAUPT}
                >
                    {laeuft && <Kreisel klein />} Nicht mitschicken
                </button>
                <button type="button" onClick={() => onSpaeter()} className={KNOPF_LEISE}>
                    Später
                </button>
            </Handlungen>
        </>
    );
};

/** Eine E-Mail, an die versendet werden kann (Name vorausgesetzt). */
const hatGueltigeEmail = (e: EmpfaengerEintrag): boolean =>
    !!(e.email || '').trim() && pruefeEmpfaengerEingabe(e.name, e.email || '') === null;

/** Dieselbe Person: gleiche Kontakt-Id, sonst gleicher Name. */
const gleichePerson = (a: EmpfaengerEintrag, b: EmpfaengerEintrag): boolean =>
    (a.kontaktId && b.kontaktId
        ? a.kontaktId === b.kontaktId
        : a.name.trim().toLowerCase() === b.name.trim().toLowerCase());

interface EmpfaengerZeile {
    schluessel: string;
    eintrag: EmpfaengerEintrag;
    grund: string;
}

const HAND = 'hand';

const EmpfaengerInhalt: React.FC<EmpfaengerKarteProps> = ({
    adressatLabel, anzahl, vorschlaege, bisher, hinweis, schreibbar, onWaehle, onSpaeter,
}) => {
    const id = useId();
    const titelId = `${id}-titel`;
    const fehlerId = `${id}-fehler`;

    // Die Vorschlaege, dazu der bisherige Eintrag, wenn er keiner von ihnen ist.
    const zeilen: EmpfaengerZeile[] = vorschlaege.map(v => ({
        schluessel: `v:${v.eintrag.kontaktId || ''}:${v.eintrag.name}:${v.eintrag.email || ''}`,
        eintrag: v.eintrag,
        grund: v.grund,
    }));
    if (bisher && !zeilen.some(z => gleichePerson(z.eintrag, bisher))) {
        zeilen.push({ schluessel: 'bisher', eintrag: bisher, grund: 'Bisher eingetragen' });
    }

    // Vorbelegung: der erste Vorschlag mit E-Mail, sonst der bisherige
    // Eintrag; ganz ohne Vorschlag gleich die Eingabe von Hand.
    const startZeile = () => {
        const mitEmail = zeilen.find(z => z.schluessel !== 'bisher' && hatGueltigeEmail(z.eintrag));
        if (mitEmail) return mitEmail;
        return bisher ? zeilen.find(z => z.schluessel === 'bisher') || zeilen.find(z => gleichePerson(z.eintrag, bisher)) || null : null;
    };
    const [wahl, setWahl] = useState<string | null>(() => {
        const z = startZeile();
        return z ? z.schluessel : zeilen.length ? null : HAND;
    });
    const [email, setEmail] = useState<string>(() => (startZeile()?.eintrag.email || '').trim());
    const [name, setName] = useState('');
    const [handEmail, setHandEmail] = useState('');
    const [versucht, setVersucht] = useState(false);

    const gewaehlt = zeilen.find(z => z.schluessel === wahl) || null;
    const brauchtEmail = !!gewaehlt && !hatGueltigeEmail(gewaehlt.eintrag);
    const eintrag: EmpfaengerEintrag | null = wahl === HAND
        ? empfaengerVonHand(name, handEmail)
        : gewaehlt
            ? (brauchtEmail ? { ...gewaehlt.eintrag, email: email.trim() } : gewaehlt.eintrag)
            : null;
    // Ohne E-Mail laesst sich nicht versenden — hier also Pflicht.
    const fehler = eintrag
        ? pruefeEmpfaengerEingabe(eintrag.name, eintrag.email || '')
            || (!(eintrag.email || '').trim() ? 'Bitte eine E-Mail-Adresse eintragen.' : null)
        : null;
    const gesperrt = !!hinweis || !schreibbar;

    const waehle = (schluessel: string) => {
        setWahl(schluessel);
        setVersucht(false);
        const z = zeilen.find(x => x.schluessel === schluessel);
        if (z) setEmail((z.eintrag.email || '').trim());
    };
    const uebernehmen = () => {
        setVersucht(true);
        if (eintrag && !fehler) onWaehle(eintrag);
    };
    const zeigeFehler = versucht && fehler;

    const emailFeld = (wert: string, setze: (s: string) => void, label: string) => {
        const feldId = `${id}-email`;
        return (
            <div>
                <label htmlFor={feldId} className="block text-xs font-bold text-slate-600 mb-1">{label}</label>
                <input
                    id={feldId}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={wert}
                    onChange={e => setze(e.target.value)}
                    aria-invalid={zeigeFehler ? true : undefined}
                    aria-describedby={zeigeFehler ? fehlerId : undefined}
                    className={FELD}
                />
            </div>
        );
    };

    const zeilenKnopf = (schluessel: string, inhalt: React.ReactNode) => {
        const an = wahl === schluessel;
        return (
            <button
                type="button"
                onClick={() => waehle(schluessel)}
                aria-pressed={an}
                className={`flex w-full min-h-14 items-start gap-3 rounded-xl border-2 px-4 py-3 text-left transition-colors ${
                    an ? 'border-blue-600 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'
                } ${FOKUS}`}
            >
                <span
                    aria-hidden="true"
                    className={`mt-0.5 inline-flex w-5 h-5 shrink-0 items-center justify-center rounded-full border-2 ${an ? 'border-blue-600' : 'border-slate-300'}`}
                >
                    {an && <span className="w-2.5 h-2.5 rounded-full bg-blue-600" />}
                </span>
                <span className="min-w-0 flex-1">{inhalt}</span>
            </button>
        );
    };

    return (
        <>
            <Kopf
                id={titelId}
                titel={anzahl === 1
                    ? `Wer bekommt die Frage an ${adressatLabel}?`
                    : `Wer bekommt die ${anzahl} Fragen an ${adressatLabel}?`}
            />
            <div role="group" aria-labelledby={titelId} className="mt-4 space-y-2">
                {zeilen.map(z => {
                    const mail = (z.eintrag.email || '').trim();
                    return (
                        <div key={z.schluessel}>
                            {zeilenKnopf(z.schluessel, (
                                <>
                                    <span className="block text-sm font-bold text-slate-800 break-words">{z.eintrag.name}</span>
                                    <span className="block text-xs text-slate-500 break-words">{z.grund}</span>
                                    <span className={`block text-xs break-all ${hatGueltigeEmail(z.eintrag) ? 'text-slate-600' : 'text-amber-700'}`}>
                                        {mail || 'keine E-Mail hinterlegt'}
                                    </span>
                                </>
                            ))}
                            {wahl === z.schluessel && brauchtEmail && (
                                <div className="mt-2 px-1">{emailFeld(email, setEmail, `E-Mail-Adresse für ${z.eintrag.name}`)}</div>
                            )}
                        </div>
                    );
                })}
                <div>
                    {zeilenKnopf(HAND, <span className="block text-sm font-bold text-slate-800">Jemand anderes</span>)}
                    {wahl === HAND && (
                        <div className="mt-2 px-1 space-y-3">
                            <div>
                                <label htmlFor={`${id}-name`} className="block text-xs font-bold text-slate-600 mb-1">Name</label>
                                <input
                                    id={`${id}-name`}
                                    type="text"
                                    autoComplete="name"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    className={FELD}
                                />
                            </div>
                            {emailFeld(handEmail, setHandEmail, 'E-Mail-Adresse')}
                        </div>
                    )}
                </div>
            </div>
            {zeigeFehler && <p id={fehlerId} role="alert" className="mt-3 text-sm font-bold text-rose-700">{fehler}</p>}
            {hinweis && <p className="mt-3 text-sm text-amber-700 leading-snug break-words">{hinweis}</p>}
            <Handlungen>
                <button
                    type="button"
                    onClick={uebernehmen}
                    disabled={gesperrt || !eintrag}
                    title={!hinweis && !schreibbar ? 'Die Zuordnung lässt sich gerade nicht speichern.' : undefined}
                    className={KNOPF_HAUPT}
                >
                    <Check className="w-5 h-5" /> Übernehmen
                </button>
                <button type="button" onClick={() => onSpaeter()} className={KNOPF_LEISE}>
                    Später
                </button>
            </Handlungen>
        </>
    );
};

/**
 * Wer bekommt die Fragen eines Adressaten? Vorschlaege zum Antippen,
 * fehlende E-Mail gleich darunter, „Jemand anderes" von Hand. Die Auswahl
 * beginnt je Adressat neu.
 */
export const EmpfaengerKarte: React.FC<EmpfaengerKarteProps> = props => <EmpfaengerInhalt key={props.adressatLabel} {...props} />;

/** Die Fragen eines Adressaten sind bereit — der Versanddialog zeigt sie vor dem Senden. */
export const VersendenKarte: React.FC<VersendenKarteProps> = ({
    adressatLabel, anzahl, empfaenger, sperrgrund, danach, laeuft, onSenden, onAndererEmpfaenger, onSpaeter,
}) => {
    const email = (empfaenger.email || '').trim();
    return (
        <>
            <Kopf
                titel={anzahl === 1 ? `1 Frage an ${adressatLabel}` : `${anzahl} Fragen an ${adressatLabel}`}
                zeile={<><span className="font-bold text-slate-800">{empfaenger.name}</span>{email ? ` · ${email}` : ''}</>}
            />
            {danach > 0 && <p className="mt-1 text-xs text-slate-500">Danach noch {danach} Empfänger.</p>}
            {sperrgrund && <p className="mt-3 text-sm text-amber-700 leading-snug break-words">{sperrgrund}</p>}
            <Handlungen>
                <button type="button" onClick={() => onSenden()} disabled={!!sperrgrund || laeuft} className={KNOPF_HAUPT}>
                    {laeuft ? <Kreisel klein /> : <Send className="w-5 h-5" />} Ansehen und senden
                </button>
                <button type="button" onClick={() => onAndererEmpfaenger()} className={KNOPF_LEISE}>
                    Anderer Empfänger
                </button>
                <button type="button" onClick={() => onSpaeter()} className={KNOPF_LEISE}>
                    Später
                </button>
            </Handlungen>
        </>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// ABSCHNITT „ANTWORTEN"
// ═══════════════════════════════════════════════════════════════════════════

/** Abgelaufene Fristen ohne Antwort festhalten — der Mensch stellt fest, nicht die App. */
export const FristenKarte: React.FC<FristenKarteProps> = ({ anzahl, laeuft, onFeststellen, onAnsehen }) => (
    <>
        <Kopf
            titel={`Bei ${anzahl} ${anzahl === 1 ? 'Frage' : 'Fragen'} ist die Frist ohne Antwort abgelaufen`}
            zeile="Halten Sie das fest, dann kann eine Bedenkenanzeige folgen."
        />
        <Handlungen>
            <button type="button" onClick={() => onFeststellen()} disabled={laeuft} className={KNOPF_HAUPT}>
                {laeuft && <Kreisel klein />} Als nicht beantwortet festhalten
            </button>
            <button type="button" onClick={() => onAnsehen()} className={KNOPF_LEISE}>
                Erst ansehen
            </button>
        </Handlungen>
    </>
);

/** Haftungsrelevante Fragen ohne Antwort → Bedenkenanzeige; sonst der liegende Entwurf. */
export const BedenkenKarte: React.FC<BedenkenKarteProps> = ({ anzahl, entwuerfe, laeuft, onVorbereiten, onEntwurfOeffnen }) => {
    if (anzahl > 0) {
        return (
            <>
                <Kopf
                    titel={anzahl === 1
                        ? '1 haftungsrelevante Frage ist ohne Antwort'
                        : `${anzahl} haftungsrelevante Fragen sind ohne Antwort`}
                    zeile="Eine Bedenkenanzeige an den Bauherrn sichert Sie ab."
                />
                <Handlungen>
                    <button type="button" onClick={() => onVorbereiten()} disabled={laeuft} className={KNOPF_HAUPT}>
                        {laeuft && <Kreisel klein />} Bedenkenanzeige vorbereiten
                    </button>
                    {entwuerfe > 0 && (
                        <button type="button" onClick={() => onEntwurfOeffnen()} className={KNOPF_LEISE}>
                            Entwurf öffnen
                        </button>
                    )}
                </Handlungen>
            </>
        );
    }
    return (
        <>
            <Kopf
                titel={entwuerfe > 1
                    ? `${entwuerfe} Bedenkenanzeigen sind noch nicht versendet`
                    : 'Eine Bedenkenanzeige ist noch nicht versendet'}
            />
            <Handlungen>
                <button type="button" onClick={() => onEntwurfOeffnen()} className={KNOPF_HAUPT}>
                    Entwurf öffnen
                </button>
            </Handlungen>
        </>
    );
};

/** Fehlende Unterlagen — die Checkliste erscheint erst nach „Jetzt anfordern". */
export const UnterlagenKarte: React.FC<UnterlagenKarteProps> = ({ kurz, children, onSpaeter }) => {
    const [offen, setOffen] = useState(false);
    return (
        <>
            <Kopf titel="Es fehlen noch Unterlagen" zeile={kurz} />
            {offen && <div className="mt-4">{children}</div>}
            <Handlungen>
                {!offen && (
                    <button type="button" onClick={() => setOffen(true)} className={KNOPF_HAUPT}>
                        Jetzt anfordern
                    </button>
                )}
                <button type="button" onClick={() => onSpaeter()} className={KNOPF_LEISE}>
                    Später
                </button>
            </Handlungen>
        </>
    );
};

/** „Fragen" oder „Frage", mit Verb. */
const wartendSatz = (n: number): string => {
    if (n <= 0) return 'Keine Frage wartet auf Antwort';
    return n === 1 ? '1 Frage wartet auf Antwort' : `${n} Fragen warten auf Antwort`;
};

/** Gerade ist nichts zu tun — was wartet, und der Weg fuer eingegangene Antworten. */
export const WartenKarte: React.FC<WartenKarteProps> = ({
    wartend, naechsteFrist, nebenbei, spaeter, nurAnsicht, antwortMailMoeglich, onAntwortMail, onVonHand, onSpaeterJetzt,
}) => {
    const frist = datumDeutsch(naechsteFrist);
    return (
        <>
            <Kopf
                icon={<Clock className="w-6 h-6 text-slate-400" />}
                titel={nurAnsicht ? 'Nur Ansicht' : 'Gerade ist nichts zu tun'}
                zeile={`${wartendSatz(wartend)}${frist ? `, nächste Frist ${frist}` : ''}`}
            />
            {nebenbei.length > 0 && (
                <ul className="mt-2 space-y-1">
                    {nebenbei.map((s, i) => <li key={`${i}:${s}`} className="text-xs text-slate-500 leading-snug break-words">{s}</li>)}
                </ul>
            )}
            {spaeter.length > 0 && (
                <div className="mt-4 flex flex-wrap items-center gap-x-3 rounded-xl bg-slate-50 px-3 py-1.5">
                    <span className="py-1.5 text-sm text-slate-700 break-words">Noch nicht verschickt: {spaeter.join(', ')}</span>
                    {!nurAnsicht && (
                        <button type="button" onClick={() => onSpaeterJetzt()} className={KNOPF_LEISE}>
                            Jetzt verschicken
                        </button>
                    )}
                </div>
            )}
            {!nurAnsicht && (
                <Handlungen>
                    <button type="button" onClick={() => onAntwortMail()} disabled={!antwortMailMoeglich} className={KNOPF_HAUPT}>
                        <Mail className="w-5 h-5" /> Antwort per E-Mail eingegangen
                    </button>
                    <button type="button" onClick={() => onVonHand()} className={KNOPF_LEISE}>
                        Von Hand eintragen
                    </button>
                </Handlungen>
            )}
        </>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// ABSCHNITT „ABSCHLUSS"
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Abschliessen mit den Folgeunterlagen als Entwurf. Die Haken folgen der
 * Vorauswahl (`istGewaehlt`); solange der Bestand gelesen wird, bleibt der
 * Knopf aus — sonst schloesse ein schneller Klick ohne Entwuerfe ab.
 */
export const AbschliessenKarte: React.FC<AbschliessenKarteProps> = ({ folgeunterlagen, restrisiken, laeuft, onAbschliessen }) => {
    const { zeilen, laden, lesefehler, isReadOnly, anlageLaeuft } = folgeunterlagen;
    const [haken, setHaken] = useState<FolgeHaken>({});
    const waehlbar = zeilen.filter(z => z.waehlbar);
    const aus = laeuft || isReadOnly || laden || anlageLaeuft;
    // Derselbe Satz wie in der Rueckfrage des Reiters — vor dem Klick, nicht danach.
    const rest = abschlussRueckfrage(restrisiken).rest;
    const zeile = laden
        ? 'Ich prüfe, welche Unterlagen schon bestehen …'
        : waehlbar.length
            ? 'Beim Abschließen lege ich diese Unterlagen als Entwurf an:'
            : lesefehler || 'Neue Entwürfe lege ich dabei nicht an.';
    return (
        <>
            <Kopf
                icon={<CheckCircle2 className="w-6 h-6 text-emerald-600" />}
                titel="Alles Haftungsrelevante ist geklärt"
                zeile={zeile}
            />
            {!laden && waehlbar.length > 0 && (
                <ul className="mt-4 space-y-1">
                    {waehlbar.map(z => (
                        <li key={z.art}>
                            <label title={z.grund} className="flex min-h-11 cursor-pointer items-center gap-3 rounded-lg px-2 py-2 hover:bg-slate-50">
                                <input
                                    type="checkbox"
                                    checked={istGewaehlt(z, haken)}
                                    onChange={e => {
                                        const an = e.target.checked;
                                        setHaken(h => ({ ...h, [z.art]: an }));
                                    }}
                                    disabled={laeuft || isReadOnly || anlageLaeuft}
                                    className="w-5 h-5 shrink-0 accent-blue-600"
                                />
                                <span className="text-sm font-bold text-slate-800">{z.titel}</span>
                            </label>
                        </li>
                    ))}
                </ul>
            )}
            {rest && <p className="mt-3 text-sm text-amber-800">{rest}</p>}
            <Handlungen>
                <button
                    type="button"
                    onClick={() => onAbschliessen(gewaehlteArten(zeilen, haken))}
                    disabled={aus}
                    title={isReadOnly ? readOnlyTitle('Abschließen') : undefined}
                    className={KNOPF_HAUPT}
                >
                    {laeuft || laden ? <Kreisel klein /> : <Check className="w-5 h-5" />} Planungsphase abschließen
                </button>
            </Handlungen>
        </>
    );
};

/** Abgeschlossen — die Folgeunterlagen bleiben erreichbar, Wiederoeffnen leise. */
export const AbgeschlossenKarte: React.FC<AbgeschlossenKarteProps> = ({ am, folgeunterlagen, isReadOnly, laeuft, onWiederOeffnen }) => {
    const tag = datumDeutsch(am);
    return (
        <>
            <Kopf
                icon={<CheckCircle2 className="w-6 h-6 text-emerald-600" />}
                titel={`Die Planungsphase ist abgeschlossen${tag ? ` am ${tag}` : ''}`}
            />
            <div className="mt-4">
                <FolgeunterlagenKarte {...folgeunterlagen} />
            </div>
            <Handlungen>
                <button
                    type="button"
                    onClick={() => onWiederOeffnen()}
                    disabled={isReadOnly || laeuft}
                    title={isReadOnly ? readOnlyTitle('Wieder öffnen') : undefined}
                    className={KNOPF_LEISE}
                >
                    Wieder öffnen
                </button>
            </Handlungen>
        </>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// HINWEIS
// ═══════════════════════════════════════════════════════════════════════════

const HINWEIS_TEXT: Record<HinweisKarteProps['art'], string> = {
    laden: 'Wird geladen …',
    fehler: 'Etwas ist schiefgegangen.',
    nur_ansicht: 'Nur Ansicht: Hier gibt es nichts zu erfassen.',
};

/** Laden, Fehler oder Nur-Ansicht: ein Satz und hoechstens ein leiser Knopf. */
export const HinweisKarte: React.FC<HinweisKarteProps> = ({ art, text, onAlleBereiche }) => (
    <>
        <Kopf
            rolle={art === 'fehler' ? 'alert' : art === 'laden' ? 'status' : undefined}
            icon={art === 'laden' ? <Kreisel /> : undefined}
            titelTon={art === 'fehler' ? 'text-rose-700' : 'text-slate-900'}
            titel={text || HINWEIS_TEXT[art]}
        />
        {onAlleBereiche && (
            <Handlungen>
                <button type="button" onClick={() => onAlleBereiche()} className={KNOPF_LEISE}>
                    Alle Bereiche ansehen <ArrowRight className="w-4 h-4" />
                </button>
            </Handlungen>
        )}
    </>
);
